import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FETCH_DASHBOARD } from 'actions/dashboardActions';
import { Spinner } from 'react-bootstrap';
import { 
  IconClipboardList, 
  IconCircleCheck, 
  IconCircleX, 
  IconClockHour4,
  IconChartPie,
  IconTrendingUp,
  IconListCheck,
  IconHistory,
  IconArrowRight,
  IconPointFilled
} from '@tabler/icons-react';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { data, loading } = useSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch({ type: FETCH_DASHBOARD });
  }, []);

  // Helper for Status Pie Chart Colors
  const statusColors = {
    APPROVED: '#38f9d7',
    CONTRACTOR: '#f6d365',
    MANAGER: '#4facfe',
    QUALITY: '#a6c1ee',
    MANAGER_APPROVED: '#a29bfe',
    PENDING: '#fda085',
    DRAFT: '#cbd5e0',
    REWORK: '#ff0844'
  };

  // SVG Pie Chart Component
  /* eslint-disable react/prop-types */
  const PieChart = ({ stats }) => {
    const total = stats.reduce((acc, curr) => acc + curr.count, 0);
    let cumulativePercent = 0;

    if (total === 0) return <div className="text-muted text-center py-5">No data available</div>;

    const slices = stats.filter(s => s.count > 0).map((slice) => {
      const percent = (slice.count / total) * 100;
      const startPercent = cumulativePercent;
      cumulativePercent += percent;

      // Simple implementation using conic-gradient via style if possible, 
      // but let's use SVG paths for better control
      return { ...slice, startPercent, percent };
    });

    return (
      <div className="d-flex align-items-center gap-4 flex-wrap flex-md-nowrap">
        <div className="pie-container" style={{ width: '180px', height: '180px', position: 'relative' }}>
          <svg viewBox="0 0 32 32" style={{ transform: 'rotate(-90deg)', borderRadius: '50%', background: '#f8f9fa' }}>
            {slices.map((slice, i) => {
              const dashArray = `${slice.percent} ${100 - slice.percent}`;
              const dashOffset = 100 - slice.startPercent;
              return (
                <circle
                  key={i}
                  r="16"
                  cx="16"
                  cy="16"
                  fill="transparent"
                  stroke={statusColors[slice.status] || '#ccc'}
                  strokeWidth="32"
                  strokeDasharray={dashArray}
                  strokeDashoffset={dashOffset}
                  style={{ transition: 'all 0.5s ease' }}
                />
              );
            })}
            <circle r="10" cx="16" cy="16" fill="white" /> {/* Donut hole */}
          </svg>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
            <span style={{ fontSize: '24px', fontWeight: '800', color: '#2d3748', display: 'block', lineHeight: 1 }}>{total}</span>
            <span style={{ fontSize: '10px', color: '#718096', fontWeight: '700', textTransform: 'uppercase' }}>Total</span>
          </div>
        </div>
        <div className="pie-legend flex-grow-1">
          {stats.filter(s => s.count > 0).map((slice, i) => (
            <div key={i} className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center gap-2">
                <IconPointFilled size={16} style={{ color: statusColors[slice.status] }} />
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>{slice.status}</span>
              </div>
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#718096' }}>{slice.count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="page-body">
        {loading ? (
          <div className="loader-container">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <div className="container-xl premium-dashboard-stats">
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="page-title m-0">Project Analytics</h2>
                <p className="text-muted small mt-1 mb-0">Unified monitoring across all commissioning and checklist cycles</p>
              </div>
              <div className="d-none d-md-block">
                <span className="badge bg-blue-lt px-3 py-2">
                  <IconHistory size={16} className="me-1" /> Last update: {new Date().toLocaleTimeString()}
                </span>
              </div>
            </div>

            {/* Quick Stats Row */}
            <div className="row g-4 mb-4">
              <div className="col-12 col-md-6 col-xl-3">
                <div className="stat-card glass-panel primary-gradient">
                  <div className="stat-icon-wrapper"><IconClipboardList size={28} /></div>
                  <div className="stat-details">
                    <span className="stat-label">Total Submissions</span>
                    <h3 className="stat-value">{data?.totalCount || 0}</h3>
                    <div className="stat-footer">
                      <span className="trend positive"><IconTrendingUp size={14} /> Global Registry</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6 col-xl-3">
                <div className="stat-card glass-panel success-gradient">
                  <div className="stat-icon-wrapper"><IconCircleCheck size={28} /></div>
                  <div className="stat-details">
                    <span className="stat-label">Total Approved</span>
                    <h3 className="stat-value">{data?.totalCountApproved || 0}</h3>
                    <div className="stat-footer">
                      <div className="progress-mini">
                        <div className="progress-bar-fill" style={{ width: `${data?.totalCount ? (data.totalCountApproved/data.totalCount)*100 : 0}%` }}></div>
                      </div>
                      <span className="progress-label">{data?.totalCount ? Math.round((data.totalCountApproved/data.totalCount)*100) : 0}%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6 col-xl-3">
                <div className="stat-card glass-panel danger-gradient">
                  <div className="stat-icon-wrapper"><IconCircleX size={28} /></div>
                  <div className="stat-details">
                    <span className="stat-label">Total Rejected</span>
                    <h3 className="stat-value">{data?.totalCountReject || 0}</h3>
                    <div className="stat-footer">
                      <div className="progress-mini">
                        <div className="progress-bar-fill" style={{ width: `${data?.totalCount ? (data.totalCountReject/data.totalCount)*100 : 0}%` }}></div>
                      </div>
                      <span className="progress-label">{data?.totalCount ? Math.round((data.totalCountReject/data.totalCount)*100) : 0}%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6 col-xl-3">
                <div className="stat-card glass-panel warning-gradient">
                  <div className="stat-icon-wrapper"><IconClockHour4 size={28} /></div>
                  <div className="stat-details">
                    <span className="stat-label">In Progress</span>
                    <h3 className="stat-value">{data?.totalCountInProgress || 0}</h3>
                    <div className="stat-footer">
                      <div className="progress-mini">
                        <div className="progress-bar-fill" style={{ width: `${data?.totalCount ? (data.totalCountInProgress/data.totalCount)*100 : 0}%` }}></div>
                      </div>
                      <span className="progress-label">{data?.totalCount ? Math.round((data.totalCountInProgress/data.totalCount)*100) : 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-4 mb-4">
              {/* Form Type Distribution */}
              <div className="col-12 col-xl-7">
                <div className="glass-panel p-4 h-100">
                  <div className="d-flex align-items-center gap-2 mb-4">
                    <IconListCheck size={20} className="text-primary" />
                    <h4 className="m-0">Form Type Distribution</h4>
                  </div>
                  <div className="form-type-grid" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {data?.formTypes?.filter(f => f.count > 0).sort((a,b) => b.count - a.count).map((type, i) => (
                      <div key={i} className="type-item">
                        <div className="d-flex justify-content-between mb-1">
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#4a5568' }}>{type.name}</span>
                          <span style={{ fontSize: '13px', fontWeight: '800', color: '#2d3748' }}>{type.count}</span>
                        </div>
                        <div className="progress-mini" style={{ height: '8px', background: '#f1f5f9' }}>
                          <div 
                            className="progress-bar-fill" 
                            style={{ 
                              width: `${(type.count / data.totalCount) * 100}%`,
                              background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)'
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                    {(!data?.formTypes || data.formTypes.filter(f => f.count > 0).length === 0) && (
                      <div className="text-muted text-center py-5">No forms submitted yet</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Status Breakdown Pie */}
              <div className="col-12 col-xl-5">
                <div className="glass-panel p-4 h-100">
                  <div className="d-flex align-items-center gap-2 mb-4">
                    <IconChartPie size={20} className="text-primary" />
                    <h4 className="m-0">Status Composition</h4>
                  </div>
                  <PieChart stats={data?.statusWise || []} />
                </div>
              </div>
            </div>

            {/* Latest Updates Section */}
            <div className="row">
              <div className="col-12">
                <div className="glass-panel p-4">
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center gap-2">
                      <IconHistory size={20} className="text-primary" />
                      <h4 className="m-0">Latest Updates</h4>
                    </div>
                    <span className="text-muted small">Most recent activity</span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-vcenter card-table table-nowrap">
                      <thead>
                        <tr>
                          <th>Form Type</th>
                          <th>Station ID</th>
                          <th>Status</th>
                          <th>Last Updated</th>
                          <th className="w-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data?.latestUpdates?.map((update, i) => (
                          <tr key={i}>
                            <td>
                              <div className="d-flex align-items-center">
                                <span className="badge bg-blue-lt me-2">{update.formName.charAt(0)}</span>
                                <span className="font-weight-medium">{update.formName}</span>
                              </div>
                            </td>
                            <td className="text-muted">{update.stationId}</td>
                            <td>
                              <span className={`badge bg-${
                                update.status?.toLowerCase() === 'approved' ? 'success' : 
                                update.status?.toLowerCase() === 'rework' ? 'danger' : 
                                update.status?.toLowerCase() === 'draft' ? 'secondary' : 'warning'
                              }-lt`}>
                                {update.status}
                              </span>
                            </td>
                            <td className="text-muted">{new Date(update.updatedAt).toLocaleString()}</td>
                            <td>
                              <IconArrowRight size={16} className="text-muted cursor-pointer" />
                            </td>
                          </tr>
                        ))}
                        {(!data?.latestUpdates || data.latestUpdates.length === 0) && (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-muted">No recent updates found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Dashboard;
